/**
 * <LateDetectionPanel /> — M3X.ATTENDANCE F.11 KEYSTONE.
 *
 * Interactive panel — HR inputs shift_id + clock_in_time + mode,
 * sees the late detection result + Khidmat outreach recommendation
 * + Article 100 rest check.
 *
 * Mode-invariant for Mutaba'aa Article 100 flagging (commandment 45).
 * Mode controls only Khidmat outreach cadence.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "../../lib/api";

interface LateResult {
  is_late: boolean;
  late_minutes: number;
  scheduled_start: string;
  grace_minutes: number;
  article_citation: string | null;
  khidmat_outreach_recommended: boolean;
  outreach_reason: string | null;
}

interface RestResult {
  violation: boolean;
  violation_minutes: number;
  article_citation: string;
  description: string;
}

export function LateDetectionPanel() {
  const { t } = useTranslation();
  const [shiftId, setShiftId] = useState("");
  const [clockInTime, setClockInTime] = useState("");
  const [mode, setMode] = useState<"conservative" | "standard" | "aggressive">("standard");
  const [lateResult, setLateResult] = useState<LateResult | null>(null);
  const [workedMin, setWorkedMin] = useState(360);
  const [breakMin, setBreakMin] = useState(0);
  const [restResult, setRestResult] = useState<RestResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function runLate() {
    if (!shiftId || !clockInTime) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api.detectLate({
        shift_id: shiftId,
        clock_in_time: new Date(clockInTime).toISOString(),
        autonomy_mode: mode,
      });
      setLateResult(r);
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function runRest() {
    setBusy(true);
    setErr(null);
    try {
      const r = await api.checkArticle100Rest({
        worked_minutes_since_last_break: workedMin,
        break_duration_minutes_taken: breakMin,
      });
      setRestResult(r);
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4 rounded-lg border border-brand-blue/30 bg-brand-blue/5 p-5">
      <div>
        <h3 className="text-base font-semibold text-brand-deep">
          ✦{" "}
          {t(
            "late_detection.title",
            "F.11 Late Detection + Article 100 Rest (Mutaba'aa)",
          )}
        </h3>
        <p className="mt-1 text-xs text-text-secondary">
          {t(
            "late_detection.subtitle",
            "Mode-invariant Mutaba'aa flagging per commandment 45. Mode controls only Khidmat outreach cadence.",
          )}
        </p>
      </div>

      {/* Late detection */}
      <div className="rounded-md border border-slate-200 bg-surface-default p-4">
        <h4 className="mb-2 text-sm font-semibold">
          {t("late_detection.late_compute", "Late detection")}
        </h4>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <input
            placeholder="Shift UUID"
            value={shiftId}
            onChange={(e) => setShiftId(e.target.value)}
            className="rounded-md border border-border-default p-2 text-xs"
          />
          <input
            type="datetime-local"
            value={clockInTime}
            onChange={(e) => setClockInTime(e.target.value)}
            className="rounded-md border border-border-default p-2 text-xs"
          />
          <select
            value={mode}
            onChange={(e) =>
              setMode(
                e.target.value as "conservative" | "standard" | "aggressive",
              )
            }
            className="rounded-md border border-border-default p-2 text-xs"
          >
            <option value="conservative">Conservative</option>
            <option value="standard">Standard</option>
            <option value="aggressive">Aggressive</option>
          </select>
        </div>
        <button
          type="button"
          onClick={runLate}
          disabled={!shiftId || !clockInTime || busy}
          className="mt-2 rounded-md bg-brand-blue px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
        >
          {busy
            ? t("late_detection.computing", "Computing…")
            : t("late_detection.run", "Compute late")}
        </button>
        {lateResult && (
          <div
            className={`mt-3 rounded p-3 text-xs ${
              lateResult.is_late
                ? "bg-amber-50 border border-amber-200 text-amber-900"
                : "bg-emerald-50 border border-emerald-200 text-emerald-900"
            }`}
          >
            {lateResult.is_late ? "⚠️" : "✓"}{" "}
            {lateResult.is_late
              ? `Late by ${lateResult.late_minutes} min (scheduled ${lateResult.scheduled_start}, grace ${lateResult.grace_minutes}m)`
              : "On time"}
            {lateResult.article_citation && (
              <div className="mt-1 text-[10px] font-mono">
                {lateResult.article_citation}
              </div>
            )}
            {lateResult.khidmat_outreach_recommended && (
              <div className="mt-1 rounded bg-brand-purple/10 px-2 py-1 text-brand-purple">
                ✦ Khidmat outreach: {lateResult.outreach_reason}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Article 100 rest */}
      <div className="rounded-md border border-slate-200 bg-surface-default p-4">
        <h4 className="mb-2 text-sm font-semibold">
          {t(
            "late_detection.rest_check",
            "Article 100 rest check (Mutaba'aa, mode-invariant)",
          )}
        </h4>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label className="block text-xs">
            <span className="text-text-secondary">
              Worked minutes since last break
            </span>
            <input
              type="number"
              min={0}
              value={workedMin}
              onChange={(e) => setWorkedMin(Number(e.target.value))}
              className="mt-1 w-full rounded-md border border-border-default p-2"
            />
          </label>
          <label className="block text-xs">
            <span className="text-text-secondary">Break minutes taken</span>
            <input
              type="number"
              min={0}
              value={breakMin}
              onChange={(e) => setBreakMin(Number(e.target.value))}
              className="mt-1 w-full rounded-md border border-border-default p-2"
            />
          </label>
        </div>
        <button
          type="button"
          onClick={runRest}
          disabled={busy}
          className="mt-2 rounded-md bg-brand-blue px-3 py-1 text-xs font-medium text-white disabled:opacity-50"
        >
          {busy
            ? t("late_detection.checking", "Checking…")
            : t("late_detection.run_rest", "Check rest")}
        </button>
        {restResult && (
          <div
            className={`mt-3 rounded p-3 text-xs ${
              restResult.violation
                ? "bg-rose-50 border border-rose-200 text-rose-900"
                : "bg-emerald-50 border border-emerald-200 text-emerald-900"
            }`}
          >
            {restResult.violation ? "✦ Mutaba'aa flag:" : "✓"}{" "}
            {restResult.description}
            <div className="mt-1 text-[10px] font-mono">
              {restResult.article_citation}
            </div>
          </div>
        )}
      </div>

      {err && (
        <div className="rounded bg-rose-50 border border-rose-200 p-2 text-xs text-rose-700">
          {err}
        </div>
      )}
    </div>
  );
}

export default LateDetectionPanel;
